import { deviceInfo } from "../utils/deviceinfo";
import { Renderer } from "./renderer";

export function createRenderer(): Renderer {
  const renderer = new Renderer({
    cameraPerspective: 75,
    cameraInitialYMove: 1500,
    backgroundColor: 0x01222f,
    foregroundColor: 0x48a9e6,
    lightColor: 0xffffff,
    lightStrenght: 2,
    blending: true,
    bloom: false,
    bloomRadius: 1,
    bloomStrength: 1,
    bloomThreshold: 0.3,
    ease: 6,
    opacity: 0.5,
  });

  // Ensure correct sizing after CSS load
  setTimeout(() => {
    renderer.resize();
  }, 0);

  window.addEventListener("resize", () => {
    renderer.resize();
    updateSwarmTarget();
  });

  const updateSwarmTarget = () => {
    // Find all visible headings (h1, h2)
    const headings = Array.from(document.querySelectorAll("main h1, main h2"));
    if (headings.length === 0) {
      renderer.setMouse(0, 0);
      renderer.setTargetWidth(0);
      return;
    }

    // If near top, focus first heading's icon (or heading if no icon)
    if (headings.length === 0) {
      renderer.setMouse(0, 0);
      renderer.setTargetWidth(0);
      return;
    }

    // If near top, focus first heading's icon (or heading if no icon)
    if (window.scrollY < 100) {
      const firstHeading = headings[0] as Element | undefined;
      if (!firstHeading) {
        renderer.setMouse(0, 0);
        renderer.setTargetWidth(0);
        return;
      }
      let iconElem: Element | null = firstHeading.previousElementSibling;
      // Look for icon-wrapper as previous sibling or parent
      if (
        !iconElem ||
        !iconElem.className ||
        !iconElem.className.toString().includes("icon-wrapper")
      ) {
        iconElem =
          firstHeading.parentElement?.querySelector(".icon-wrapper") ?? null;
      }
      const targetElem: Element =
        iconElem && typeof iconElem.getBoundingClientRect === "function"
          ? iconElem
          : firstHeading;
      if (
        !targetElem ||
        typeof targetElem.getBoundingClientRect !== "function"
      ) {
        renderer.setMouse(0, 0);
        renderer.setTargetWidth(0);
        return;
      }
      const rect = targetElem.getBoundingClientRect();
      const boxSize = Math.min(rect.width, rect.height);
      const x = rect.left + rect.width / 2 - window.innerWidth / 2;
      const y = rect.top + rect.height / 2 - window.innerHeight / 2;
      renderer.setMouse(x, y);
      renderer.setTargetWidth(boxSize);
      return;
    }

    // Find heading closest to vertical center
    const centerY = window.innerHeight / 2;
    let closestHeading: Element | undefined = headings[0] as
      | Element
      | undefined;
    let minDistance = Infinity;
    headings.forEach((heading) => {
      if (!heading) return;
      const rect = heading.getBoundingClientRect();
      const headingCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(headingCenterY - centerY);
      if (distance < minDistance) {
        minDistance = distance;
        closestHeading = heading;
      }
    });

    if (!closestHeading) {
      renderer.setMouse(0, 0);
      renderer.setTargetWidth(0);
      return;
    }

    // Prefer icon-wrapper near the heading if present
    let iconElem: Element | null = closestHeading.previousElementSibling;
    if (
      !iconElem ||
      !iconElem.className ||
      !iconElem.className.toString().includes("icon-wrapper")
    ) {
      iconElem =
        closestHeading.parentElement?.querySelector(".icon-wrapper") ?? null;
    }
    const targetElem: Element =
      iconElem && typeof iconElem.getBoundingClientRect === "function"
        ? iconElem
        : closestHeading;
    if (!targetElem || typeof targetElem.getBoundingClientRect !== "function") {
      renderer.setMouse(0, 0);
      renderer.setTargetWidth(0);
      return;
    }
    const rect = targetElem.getBoundingClientRect();
    const boxSize = Math.min(rect.width, rect.height);
    const x = rect.left + rect.width / 2 - window.innerWidth / 2;
    const y = rect.top + rect.height / 2 - window.innerHeight / 2;
    renderer.setMouse(x, y);
    renderer.setTargetWidth(boxSize);
    renderer.setMouse(x, y);
    renderer.setTargetWidth(rect.width);
  };

  // Initial update loop to catch animations (like fade-up)
  const start = Date.now();
  const loop = () => {
    updateSwarmTarget();
    if (Date.now() - start < 3000) {
      requestAnimationFrame(loop);
    }
  };
  loop();

  // Continuous polling to keep swarm on target (handles CSS animations, lazy loading, etc.)
  setInterval(() => {
    updateSwarmTarget();
  }, 100);

  // Watch for DOM changes (tab switching)
  const observer = new MutationObserver(() => {
    // Restart loop on DOM change to catch new animations
    const restart = Date.now();
    const restartLoop = () => {
      updateSwarmTarget();
      if (Date.now() - restart < 2000) {
        requestAnimationFrame(restartLoop);
      }
    };
    restartLoop();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener("scroll", () => {
    renderer.setScroll(deviceInfo.getScrollPercent());
    updateSwarmTarget();
  });

  return renderer;
}
